// Google Gemini AI Provider  
// Uses official @google/genai SDK
// Handles Gemini 3 Pro with streaming and thinking capabilities

import { getOrCreateDeviceId, getSessionId } from '../utils'

// Gemini 3 Pro Provider via Vertex AI
// Uses Google Gen AI SDK with service account authentication

let currentAbortController = null

// Default configuration
const DEFAULT_PROJECT_ID = 'p5-ai-studio' // Override via env or user settings
const DEFAULT_LOCATION = 'us-central1'

/**
 * Abort current Gemini request
 */
export function abortGemini() {
  if (currentAbortController) {
    currentAbortController.abort()
    currentAbortController = null
  }
}

// Alias for consistency with other providers
export const stopGemini = abortGemini

/**
 * Main Gemini 3 Pro call function (Vertex AI)
 * @param {Array} messages - Messages with role and content
 * @param {string|null} userApiKey - Service account credentials or null for server
 * @param {string} model - Model name (gemini-3-pro-preview)
 * @param {string} thinkingLevel - 'low' or 'high'
 * @param {function} onThinking - Callback for real-time thinking
 * @returns {Promise<{content: string, reasoning: string, usage: object}>}
 */
export async function callGemini(messages, userApiKey, model = 'gemini-3-pro-preview', thinkingLevel = 'high', onThinking = null) {
  currentAbortController = new AbortController()
  
  console.log('🔵 Gemini 3 Pro (Vertex AI):', { model, thinkingLevel, hasKey: !!userApiKey })
  
  if (userApiKey) {
    return await callDirectAPI(messages, userApiKey, model, thinkingLevel, currentAbortController.signal, onThinking)
  }
  
  // Server streaming (through Cloudflare function)
  return await callThroughServer(messages, model, thinkingLevel, onThinking)
}

// Server streaming (through Cloudflare function)
async function callThroughServer(messages, model, thinkingLevel, onThinking) {
  // Get device ID for job persistence
  const deviceId = getOrCreateDeviceId()
  const sessionId = getSessionId()
  
  try {
    const response = await fetch('/api/ai-chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Connection': 'keep-alive'
      },
      body: JSON.stringify({
        messages,
        provider: 'gemini',
        model,
        thinkingLevel,
        // Job persistence fields
        deviceId,
        sessionId
      }),
      signal: currentAbortController.signal,
      cache: 'no-store'
    })
    
    return await handleStreamingResponse(response, onThinking)
  } catch (error) {
    console.error('Gemini server error:', error)
    throw error
  }
}

// Direct API call using standard Gemini API
async function callDirectAPI(messages, apiKey, model, thinkingLevel, signal, onThinking) {
  // Import the CORRECT Gemini API SDK (@google/generative-ai)
  const { GoogleGenerativeAI } = await import('@google/generative-ai')
  
  // Initialize client with API key
  const genAI = new GoogleGenerativeAI(apiKey)
  
  // Extract system instruction and conversation
  const systemMessage = messages.find(m => m.role === 'system')
  const conversationMessages = messages.filter(m => m.role !== 'system')
  
  // Build contents string (combine messages)
  let contentsText = ''
  if (systemMessage) {
    contentsText = `${systemMessage.content}\n\n`
  }
  
  // Format conversation
  for (const msg of conversationMessages) {
    const role = msg.role === 'assistant' ? 'Assistant' : 'User'
    contentsText += `${role}: ${msg.content}\n\n`
  }
  
  try {
    // Create model instance
    const geminiModel = genAI.getGenerativeModel({ model })
    
    // Use generateContentStream for streaming
    const result = await geminiModel.generateContentStream(contentsText)
    
    let content = ''
    let thinking = ''
    
    // Process stream
    for await (const chunk of result.stream) {
      if (signal && signal.aborted) {
        throw new Error('Request cancelled')
      }
      
      // Extract text from chunk using text() method
      const text = chunk.text()
      if (text) {
        content += text
      }
      
      // Check for thinking in response (if available)
      if (chunk.thought && onThinking) {
        thinking += chunk.thought
        onThinking(chunk.thought)
      }
    }
    
    return {
      content,
      reasoning: thinking,
      usage: null
    }
  } catch (error) {
    if (signal && signal.aborted) {
      throw new Error('Request cancelled')
    }
    console.error('Gemini API error:', error)
    throw new Error(`Gemini 3 Pro error: ${error.message}`)
  }
}

// Handle streaming response from server
async function handleStreamingResponse(response, onThinking) {
  console.log('🔵 Gemini streaming response:', { 
    ok: response.ok, 
    status: response.status,
    contentType: response.headers.get('content-type')
  })

  if (!response.ok) {
    const errorText = await response.text()
    console.error('❌ Gemini server error:', errorText)
    const error = response.status === 504 
      ? '⏱️ Response timed out. Add your API key in Settings.'
      : errorText || 'Gemini request failed'
    throw new Error(error)
  }

  if (!response.body) {
    throw new Error('Streaming not supported')
  }

  // Parse SSE stream
  let content = ''
  let reasoning = ''
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let chunkCount = 0

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) {
        console.log(`✅ Stream complete. Received ${chunkCount} chunks, ${content.length} chars`)
        break
      }

      chunkCount++
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''
      
      for (const line of lines) {
        if (!line.trim() || !line.startsWith('data: ')) continue
        
        const dataStr = line.slice(6)
        if (dataStr === '[DONE]') continue

        try {
          const data = JSON.parse(dataStr)
          
          if (data.type === 'delta' && data.text) {
            content += data.text
          } else if (data.type === 'thinking' && onThinking) {
            const thinkingDelta = data.text || ''
            if (thinkingDelta) {
              console.log('🧠 Thinking delta:', thinkingDelta.substring(0, 50) + '...')
              onThinking(thinkingDelta)
            }
          } else if (data.type === 'error') {
            throw new Error(data.message || 'Stream error')
          }
        } catch (e) {
          console.warn('Parse error:', e, 'Line:', line)
        }
      }
    }
  } catch (error) {
    console.error('❌ Stream error:', error)
    if (content.length > 0) {
      console.log('⚠️  Returning partial content:', content.length, 'chars')
      return { content, reasoning, usage: null }
    }
    throw error
  }

  if (!content) {
    console.error('❌ No content received after', chunkCount, 'chunks')
    throw new Error('No response received from Gemini. Please check your API key or try using "Use Server Keys" option.')
  }

  return { content, reasoning, usage: null }
}
